// Live blockchain wallet connectivity utilities
import { toast } from 'sonner';
import { connectWalletConnect, getWalletConnectProvider } from './walletConnect';

// RPC endpoints - Using paid RPC APIs (QuickNode, NOWNodes)
// MM2 stays on VPS, all other chains use external RPC APIs
import { VPS_RPC_CONFIG, getRPCEndpointWithFallback } from './rpcConfig';

export const RPC_ENDPOINTS = {
  // MM2/KDF (Komodo DeFi Framework) - On VPS
  mm2: {
    primary: VPS_RPC_CONFIG.mm2.url,
  },
  // Ravencoin - VPS Node (http://209.126.81.169:8766)
  ravencoin: {
    primary: VPS_RPC_CONFIG.ravencoin.url,
  },
  // Ethereum - QuickNode primary, public RPC fallback
  ethereum: {
    primary: (VPS_RPC_CONFIG.ethereum as any).primary || (VPS_RPC_CONFIG.ethereum as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.ethereum as any).fallback || '',
  },
  // Bitcoin - QuickNode primary, public RPC fallback
  bitcoin: {
    primary: (VPS_RPC_CONFIG.bitcoin as any).primary || (VPS_RPC_CONFIG.bitcoin as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.bitcoin as any).fallback || '',
  },
  // Binance Smart Chain - QuickNode primary, public RPC fallback
  binanceSmartChain: {
    primary: (VPS_RPC_CONFIG.binanceSmartChain as any).primary || (VPS_RPC_CONFIG.binanceSmartChain as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.binanceSmartChain as any).fallback || '',
  },
  // Polygon - QuickNode primary, public RPC fallback
  polygon: {
    primary: (VPS_RPC_CONFIG.polygon as any).primary || (VPS_RPC_CONFIG.polygon as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.polygon as any).fallback || '',
  },
  // Avalanche - QuickNode primary, public RPC fallback
  avalanche: {
    primary: (VPS_RPC_CONFIG.avalanche as any).primary || (VPS_RPC_CONFIG.avalanche as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.avalanche as any).fallback || '',
  },
  // Arbitrum - QuickNode primary, public RPC fallback
  arbitrum: {
    primary: (VPS_RPC_CONFIG.arbitrum as any).primary || (VPS_RPC_CONFIG.arbitrum as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.arbitrum as any).fallback || '',
  },
  // Base - Public RPC primary, QuickNode fallback
  base: {
    primary: (VPS_RPC_CONFIG.base as any).primary || (VPS_RPC_CONFIG.base as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.base as any).fallback || '',
  },
  // Optimism - Public RPC primary, QuickNode fallback
  optimism: {
    primary: (VPS_RPC_CONFIG.optimism as any).primary || (VPS_RPC_CONFIG.optimism as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.optimism as any).fallback || '',
  },
  // Hyperliquid - Public RPC primary, QuickNode fallback
  hyperliquid: {
    primary: (VPS_RPC_CONFIG.hyperliquid as any).primary || (VPS_RPC_CONFIG.hyperliquid as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.hyperliquid as any).fallback || '',
  },
  // Fantom - Public RPC primary, QuickNode fallback
  fantom: {
    primary: (VPS_RPC_CONFIG.fantom as any).primary || (VPS_RPC_CONFIG.fantom as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.fantom as any).fallback || '',
  },
  // zkSync Era - Public RPC primary, QuickNode fallback
  zkSyncEra: {
    primary: (VPS_RPC_CONFIG.zkSyncEra as any).primary || (VPS_RPC_CONFIG.zkSyncEra as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.zkSyncEra as any).fallback || '',
  },
  // Linea - Public RPC primary, QuickNode fallback
  linea: {
    primary: (VPS_RPC_CONFIG.linea as any).primary || (VPS_RPC_CONFIG.linea as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.linea as any).fallback || '',
  },
  // Scroll - Public RPC primary, QuickNode fallback
  scroll: {
    primary: (VPS_RPC_CONFIG.scroll as any).primary || (VPS_RPC_CONFIG.scroll as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.scroll as any).fallback || '',
  },
  // Mantle - Public RPC primary, QuickNode fallback
  mantle: {
    primary: (VPS_RPC_CONFIG.mantle as any).primary || (VPS_RPC_CONFIG.mantle as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.mantle as any).fallback || '',
  },
  // Blast - Public RPC primary, QuickNode fallback
  blast: {
    primary: (VPS_RPC_CONFIG.blast as any).primary || (VPS_RPC_CONFIG.blast as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.blast as any).fallback || '',
  },
  // Celo - Public RPC primary, QuickNode fallback
  celo: {
    primary: (VPS_RPC_CONFIG.celo as any).primary || (VPS_RPC_CONFIG.celo as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.celo as any).fallback || '',
  },
  // Gnosis Chain - Public RPC primary, QuickNode fallback
  gnosis: {
    primary: (VPS_RPC_CONFIG.gnosis as any).primary || (VPS_RPC_CONFIG.gnosis as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.gnosis as any).fallback || '',
  },
  // Polygon zkEVM - Public RPC primary, QuickNode fallback
  polygonZkEVM: {
    primary: (VPS_RPC_CONFIG.polygonZkEVM as any).primary || (VPS_RPC_CONFIG.polygonZkEVM as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.polygonZkEVM as any).fallback || '',
  },
  // Mode Network - Public RPC primary, QuickNode fallback
  mode: {
    primary: (VPS_RPC_CONFIG.mode as any).primary || (VPS_RPC_CONFIG.mode as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.mode as any).fallback || '',
  },
  // Solana - Public RPC primary, Ankr fallback
  solana: {
    primary: (VPS_RPC_CONFIG.solana as any).primary || (VPS_RPC_CONFIG.solana as any).fallback || 'https://api.mainnet-beta.solana.com',
    fallback: (VPS_RPC_CONFIG.solana as any).fallback || 'https://solana-api.projectserum.com',
  },
  // Litecoin - QuickNode primary, public RPC fallback
  litecoin: {
    primary: (VPS_RPC_CONFIG.litecoin as any).primary || (VPS_RPC_CONFIG.litecoin as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.litecoin as any).fallback || '',
  },
  // Dogecoin - QuickNode primary, public RPC fallback
  dogecoin: {
    primary: (VPS_RPC_CONFIG.dogecoin as any).primary || (VPS_RPC_CONFIG.dogecoin as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.dogecoin as any).fallback || '',
  },
  // Bitcoin Cash - QuickNode primary, public RPC fallback
  bitcoinCash: {
    primary: (VPS_RPC_CONFIG.bitcoinCash as any).primary || (VPS_RPC_CONFIG.bitcoinCash as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.bitcoinCash as any).fallback || '',
  },
  // Zcash - Public RPC primary, QuickNode fallback
  zcash: {
    primary: (VPS_RPC_CONFIG.zcash as any).primary || (VPS_RPC_CONFIG.zcash as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.zcash as any).fallback || '',
  },
  // Dash - Public RPC primary, QuickNode fallback (MM2 support)
  dash: {
    primary: (VPS_RPC_CONFIG.dash as any).primary || (VPS_RPC_CONFIG.dash as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.dash as any).fallback || '',
  },
  // Komodo - Public RPC primary, QuickNode fallback (MM2 native chain)
  komodo: {
    primary: (VPS_RPC_CONFIG.komodo as any).primary || (VPS_RPC_CONFIG.komodo as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.komodo as any).fallback || '',
  },
  // Qtum - Public RPC primary, QuickNode fallback (MM2 support)
  qtum: {
    primary: (VPS_RPC_CONFIG.qtum as any).primary || (VPS_RPC_CONFIG.qtum as any).fallback || '',
    fallback: (VPS_RPC_CONFIG.qtum as any).fallback || '',
  },
  // XRP Ledger - Public RPC endpoints (for future wallet integration)
  xrp: {
    primary: 'https://xrplcluster.com/',
    fallback: 'https://s1.ripple.com:51234/',
  },
  // Monero - Public RPC endpoints (for future wallet integration)
  monero: {
    primary: 'http://node.moneroworld.com:18089/json_rpc',
    fallback: 'http://opennode.xmr-tw.org:18089/json_rpc',
  },
  // Internet Computer - Public API endpoints (for future wallet integration)
  icp: {
    primary: 'https://ic0.app', // ICP boundary node
    fallback: 'https://icp-api.io', // Alternative ICP API
  },
};

// Module-level logging removed - only log in development to avoid console spam
// console.log('🔗 KawSwap RPC Configuration Loaded');
// console.log('📡 Ravencoin Endpoint:', RPC_ENDPOINTS.ravencoin.primary);
// console.log('✅ Using VPS Ravencoin Node');

// Optimized timeouts for near-instant interaction
const CONNECTION_TIMEOUT = 10000; // 10 seconds - reduced for faster feedback
const RPC_TIMEOUT = 3000; // 3 seconds - aggressive timeout for instant feel
const MOBILE_CONNECTION_TIMEOUT = 15000; // 15 seconds - reduced for mobile
const EXTENSION_CONNECTION_TIMEOUT = 2000; // 2 seconds - very fast for browser extensions
const CHAIN_ID_TIMEOUT = 1500; // 1.5 seconds - ultra-quick chain ID fetch

export class WalletConnectionError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = 'WalletConnectionError';
  }
}

function createTimeout(ms: number): Promise<never> {
  return new Promise((_, reject) =>
    setTimeout(() => reject(new WalletConnectionError('Connection timeout', 'TIMEOUT')), ms)
  );
}

// Optimized browser extension connection (faster than WalletConnect for desktop)
async function connectBrowserExtension(): Promise<{ address: string; chainId: string } | null> {
  if (!window.ethereum) {
    return null;
  }

  try {
    // Fast connection with optimized timeout
    const accounts = await Promise.race([
      window.ethereum.request({ method: 'eth_requestAccounts' }),
      createTimeout(EXTENSION_CONNECTION_TIMEOUT),
    ]) as string[];

    if (!accounts || accounts.length === 0) {
      return null;
    }

    // Get chain ID with timeout
    const chainId = await Promise.race([
      window.ethereum.request({ method: 'eth_chainId' }),
      createTimeout(CHAIN_ID_TIMEOUT),
    ]) as string;

    return {
      address: accounts[0],
      chainId: chainId,
    };
  } catch (error: any) {
    // User rejection - don't fall through to WalletConnect
    if (error.code === 4001) {
      throw new WalletConnectionError('User rejected the connection request.', 'USER_REJECTED');
    }
    // Other errors - return null to fall through to WalletConnect
    return null;
  }
}

export async function connectWeb3Wallet(): Promise<{ address: string; chainId: string }> {
  // Optimized connection flow:
  // 1. Try browser extension first (fastest, ~1-2 seconds)
  // 2. Fall back to WalletConnect v2 (mobile/desktop, ~5-10 seconds)
  // WalletConnect v2 handles:
  // - Browser extensions (MetaMask, Trust Wallet extension) - direct connection
  // - Mobile apps - QR code scanning with deep linking
  // - Mobile browsers - universal links and deep linking
  
  // Detect mobile for optimized timeout
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  // On desktop, try browser extension first for peak responsiveness
  if (!isMobile && window.ethereum) {
    try {
      const extensionResult = await connectBrowserExtension();
      if (extensionResult) {
        // Check if connected to mainnet
        if (extensionResult.chainId !== '0x1') {
          toast.warning('Network Notice', {
            description: `Connected to chain ${extensionResult.chainId}. For best experience, please switch to Ethereum Mainnet.`,
          });
        }
        return extensionResult;
      }
    } catch (error: any) {
      // If user rejected, throw immediately
      if (error instanceof WalletConnectionError && error.code === 'USER_REJECTED') {
        throw error;
      }
      // Other errors - fall through to WalletConnect
    }
  }
  
  // Use WalletConnect v2 (better for mobile, fallback for desktop)
  const timeout = isMobile ? MOBILE_CONNECTION_TIMEOUT : CONNECTION_TIMEOUT;
  
  try {
    const result = await Promise.race([
      connectWalletConnect(),
      createTimeout(timeout),
    ]);

    // Check if connected to mainnet
    if (result.chainId !== '0x1') {
      toast.warning('Network Notice', {
        description: `Connected to chain ${result.chainId}. For best experience, please switch to Ethereum Mainnet.`,
      });
    }

    return result;
  } catch (error: any) {
    if (error instanceof WalletConnectionError) {
      throw error;
    }
    if (error.code === 4001) {
      throw new WalletConnectionError('User rejected the connection request.', 'USER_REJECTED');
    }
    if (error.code === -32002) {
      throw new WalletConnectionError(
        'Connection request already pending. Please check your wallet.',
        'REQUEST_PENDING'
      );
    }
    // Handle frames-disallowed error
    if (error?.message?.includes('frames-disallowed') || error?.message?.includes('dapp.frames-disallowed') || error?.code === 'FRAMES_DISALLOWED') {
      throw new WalletConnectionError(
        'WalletConnect configuration error: Domain not whitelisted. Please add kawswap.io to your WalletConnect Cloud project.',
        'FRAMES_DISALLOWED'
      );
    }
    
    throw new WalletConnectionError(
      error?.message || 'Failed to connect wallet via WalletConnect',
      error?.code || 'CONNECTION_FAILED'
    );
  }
}

export async function fetchWeb3Balance(address: string, tokenSymbol: string): Promise<number> {
  // Prefer window.ethereum (browser extension) over WalletConnect for balance checks
  // WalletConnect provider is mainly for connection, browser extensions are faster for RPC calls
  const provider = window.ethereum || getWalletConnectProvider();
  
  if (!provider) {
    throw new WalletConnectionError('Web3 provider not available', 'NO_PROVIDER');
  }

  try {
    if (tokenSymbol === 'ETH') {
      try {
        // Prefer window.ethereum (browser extension) for faster RPC calls
        const provider = window.ethereum || getWalletConnectProvider();
        if (!provider) {
          throw new WalletConnectionError('Web3 provider not available', 'NO_PROVIDER');
        }
        
        const balance = await Promise.race([
          provider.request({
            method: 'eth_getBalance',
            params: [address, 'latest'],
          }),
          createTimeout(RPC_TIMEOUT),
        ]);
        return parseInt(balance, 16) / 1e18;
      } catch (primaryError) {
        console.error('VPS Ethereum RPC failed:', primaryError);
        throw new WalletConnectionError('Failed to fetch balance from VPS Ethereum node', 'RPC_FAILED');
      }
    } else {
      return 0;
    }
  } catch (error: any) {
    if (error instanceof WalletConnectionError) {
      throw error;
    }
    console.error('Failed to fetch balance:', error);
    throw new WalletConnectionError('Failed to fetch balance', 'BALANCE_FETCH_FAILED');
  }
}

export async function fetchRavencoinBalance(address: string): Promise<number> {
  // Fetch balance from Ravencoin Core RPC (VPS node for balance queries)
  const [primaryUrl, fallbackUrl] = getRPCEndpointWithFallback('ravencoin');
  
  if (!primaryUrl && !fallbackUrl) {
    console.error('❌ No Ravencoin RPC endpoint configured');
    return 0;
  }

  const requestBody = {
    jsonrpc: '2.0',
    method: 'getaddressbalance',
    params: [{ addresses: [address] }],
    id: 1,
  };

  // Try primary first (VPS node with auth)
  if (primaryUrl) {
    try {
      const auth = btoa(`${VPS_RPC_CONFIG.ravencoin.username}:${VPS_RPC_CONFIG.ravencoin.password}`);
      
      const response = await Promise.race([
        fetch(primaryUrl, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Basic ${auth}`,
          },
          body: JSON.stringify(requestBody),
        }),
        createTimeout(RPC_TIMEOUT),
      ]);

      if (response.ok) {
        const data = await response.json();
        if (!data.error && data.result && typeof data.result.balance === 'number') {
          const balance = data.result.balance / 100000000;
          if (import.meta.env.DEV) {
            console.log('✅ Ravencoin Balance Retrieved:', balance, 'RVN');
          }
          return balance;
        }
        // If we got a response but no balance, continue to fallback
        if (data.error) {
          throw new Error(data.error.message || 'RPC error');
        }
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error: any) {
      // Primary failed, try fallback
      if (import.meta.env.DEV) {
        console.warn('⚠️ Primary Ravencoin RPC failed, trying fallback:', error?.message || error);
      }
    }
  }

  // Try fallback (public RPC, no auth)
  if (fallbackUrl) {
    try {
      const response = await Promise.race([
        fetch(fallbackUrl, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }),
        createTimeout(RPC_TIMEOUT),
      ]);

      if (response.ok) {
        const data = await response.json();
        if (!data.error && data.result && typeof data.result.balance === 'number') {
          const balance = data.result.balance / 100000000;
          if (import.meta.env.DEV) {
            console.log('✅ Ravencoin Balance Retrieved (fallback):', balance, 'RVN');
          }
          return balance;
        }
        if (data.error) {
          throw new WalletConnectionError(
            `Ravencoin RPC error: ${data.error.message || 'Unknown error'}`,
            'RPC_ERROR'
          );
        }
      } else {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
    } catch (error: any) {
      if (error instanceof WalletConnectionError) {
        throw error;
      }
      const errorMessage = error?.message || String(error) || 'Unknown error';
      if (import.meta.env.DEV) {
        console.error('❌ Failed to fetch Ravencoin balance (both endpoints failed):', errorMessage);
      }
    }
  }

  // Both endpoints failed
  return 0;
}

export interface TransactionParams {
  from: string;
  to: string;
  value?: string;
  data?: string;
  gas?: string;
  gasPrice?: string;
  maxFeePerGas?: string;
  maxPriorityFeePerGas?: string;
  nonce?: string;
}

export async function signWeb3Transaction(
  from: string,
  to: string,
  value: string,
  data?: string
): Promise<string>;
export async function signWeb3Transaction(
  params: TransactionParams
): Promise<string>;
export async function signWeb3Transaction(
  fromOrParams: string | TransactionParams,
  to?: string,
  value?: string,
  data?: string
): Promise<string> {
  // Prefer window.ethereum (browser extension) for transaction signing
  const provider = window.ethereum || getWalletConnectProvider();
  if (!provider) {
    throw new WalletConnectionError('Web3 provider not available', 'NO_PROVIDER');
  }

  try {
    // Handle both function signatures
    let txParams: TransactionParams;
    if (typeof fromOrParams === 'string') {
      // Legacy signature: signWeb3Transaction(from, to, value, data)
      txParams = {
        from: fromOrParams,
        to: to!,
        value: value ? '0x' + Math.floor(parseFloat(value) * 1e18).toString(16) : '0x0',
        data: data || '0x',
      };
    } else {
      // New signature: signWeb3Transaction({ from, to, value, data, gas, ... })
      txParams = fromOrParams;
      // Convert value to hex if it's a decimal string
      if (txParams.value && !txParams.value.startsWith('0x')) {
        txParams.value = '0x' + Math.floor(parseFloat(txParams.value) * 1e18).toString(16);
      }
    }

    const txHash = await Promise.race([
      provider.request({
        method: 'eth_sendTransaction',
        params: [txParams],
      }),
      createTimeout(CONNECTION_TIMEOUT),
    ]);
    return txHash as string;
  } catch (error: any) {
    if (error instanceof WalletConnectionError) {
      throw error;
    }
    if (error.code === 4001) {
      throw new WalletConnectionError('User rejected the transaction.', 'USER_REJECTED');
    }
    throw new WalletConnectionError(error.message || 'Transaction failed', error.code);
  }
}

export async function verifyWalletSignature(address: string, message: string): Promise<string> {
  // Prefer window.ethereum (browser extension) for signature requests
  const provider = window.ethereum || getWalletConnectProvider();
  if (!provider) {
    throw new WalletConnectionError('Web3 provider not available', 'NO_PROVIDER');
  }

  try {
    const signature = await Promise.race([
      provider.request({
        method: 'personal_sign',
        params: [message, address],
      }),
      createTimeout(CONNECTION_TIMEOUT),
    ]);
    return signature;
  } catch (error: any) {
    if (error instanceof WalletConnectionError) {
      throw error;
    }
    if (error.code === 4001) {
      throw new WalletConnectionError('User rejected signature request.', 'USER_REJECTED');
    }
    throw new WalletConnectionError(error.message || 'Signature failed', error.code);
  }
}

// Helper to detect all installed Web3 wallets
export function detectInstalledWallets(): {
  metamask: boolean;
  trust: boolean;
  coinbase: boolean;
  others: boolean;
  providers: any[];
} {
  const providers: any[] = [];
  let metamask = false;
  let trust = false;
  let coinbase = false;
  let others = false;

  // Check if window.ethereum exists
  if (window.ethereum) {
    // Modern wallets expose providers array
    if (Array.isArray((window.ethereum as any).providers)) {
      (window.ethereum as any).providers.forEach((provider: any) => {
        providers.push(provider);
        if (provider.isMetaMask) metamask = true;
        if (provider.isTrust) trust = true;
        if (provider.isCoinbaseWallet) coinbase = true;
        if (!provider.isMetaMask && !provider.isTrust && !provider.isCoinbaseWallet) {
          others = true;
        }
      });
    } else {
      // Single provider or legacy setup
      providers.push(window.ethereum);
      if ((window.ethereum as any).isMetaMask) metamask = true;
      if ((window.ethereum as any).isTrust) trust = true;
      if ((window.ethereum as any).isCoinbaseWallet) coinbase = true;
      if (!(window.ethereum as any).isMetaMask && 
          !(window.ethereum as any).isTrust && 
          !(window.ethereum as any).isCoinbaseWallet) {
        others = true;
      }
    }
  }

  return { metamask, trust, coinbase, others, providers };
}

/**
 * Check if KawSwap Wallet Bridge extension is available
 */
function hasExtension(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  
  const kawProxy = (window as any).kawProxy;
  return kawProxy !== undefined && 
         typeof kawProxy.detect === 'function' && 
         kawProxy.detect() === true;
}

export async function connectRavencoinCore(): Promise<{ address: string }> {
  // Connect to Ravencoin Core wallet (localhost:8766) - full node with proper RPC
  // NO VPS FALLBACK - VPS is for backend operations only (balance queries, RPC)
  // Users must use local Ravencoin Core wallet or enter addresses manually for swaps
  
  // Check if we're on localhost (http://localhost) - direct connection should work
  const isLocalhost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1';
  const isHttps = window.location.protocol === 'https:';
  
  try {
    // Connect to local Ravencoin Core (localhost:8766)
    try {
      // On HTTPS sites, try extension first (bypasses CORS)
      // On localhost (HTTP), try direct connection first (no CORS issue)
      if (isHttps && hasExtension()) {
        const kawProxy = (window as any).kawProxy;
        const commonCredentials = [
          { user: 'rpcuser', pass: 'rpcpass' },
          { user: 'ravencoin', pass: 'ravencoin' },
          { user: 'admin', pass: 'admin' },
        ];
        
        for (const creds of commonCredentials) {
          try {
            // Try modern method first (getnewaddress), fallback to getaccountaddress for compatibility
            let result: string | null = null;
            
            // Try getnewaddress first (modern Ravencoin Core method)
            try {
              result = await kawProxy.call('getnewaddress', [''], {
                wallet: 'ravencoin-core',
                rpcUser: creds.user,
                rpcPass: creds.pass,
              });
            } catch (newAddrError: any) {
              // Fallback to getaccountaddress for older Ravencoin Core versions
              if (newAddrError.error && newAddrError.error.code !== 'AUTH_FAILED') {
                try {
                  result = await kawProxy.call('getaccountaddress', [''], {
                    wallet: 'ravencoin-core',
                    rpcUser: creds.user,
                    rpcPass: creds.pass,
                  });
                } catch (oldAddrError: any) {
                  // Both methods failed, try next credentials
                  if (oldAddrError.error && oldAddrError.error.code === 'AUTH_FAILED') {
                    continue; // Wrong credentials, try next
                  }
                  continue;
                }
              } else {
                // Auth failed, try next credentials
                continue;
              }
            }
            
            if (result && typeof result === 'string') {
              const address = result;
              if (address.startsWith('R') && address.length === 34) {
                console.log('✅ [Ravencoin Wallet] Connected to local Ravencoin Core wallet via extension:', address);
                toast.success('Ravencoin Core Connected', {
                  description: `Using local Ravencoin Core wallet for transaction signing`,
                  duration: 5000,
                });
                return { address };
              }
            }
          } catch (error: any) {
            // Try next credential set
            if (error.error && error.error.code === 'AUTH_FAILED') {
              continue; // Wrong credentials, try next
            }
            // Other errors - might be connection refused, etc.
            continue;
          }
        }
      }
      
      // Try direct connection to localhost (works on localhost, fails on HTTPS)
      const localRpcUrl = 'http://localhost:8766';
      
      // Try common RPC credentials (user may have set these)
      const commonCredentials = [
        { user: 'rpcuser', pass: 'rpcpass' },
        { user: 'ravencoin', pass: 'ravencoin' },
        { user: 'admin', pass: 'admin' },
      ];
      
      for (const creds of commonCredentials) {
        try {
          const auth = btoa(`${creds.user}:${creds.pass}`);
          
          // Try getnewaddress first (modern method)
          let requestBody = {
            jsonrpc: '2.0',
            method: 'getnewaddress',
            params: [''], // Default label
            id: 1,
          };
          
          let response = await Promise.race([
            fetch(localRpcUrl, {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Basic ${auth}`,
              },
              body: JSON.stringify(requestBody),
            }),
            createTimeout(3000), // 3 second timeout for local
          ]);
          
          let address: string | null = null;
          
          if (response.ok) {
            const data = await response.json();
            if (!data.error && data.result && typeof data.result === 'string') {
              address = data.result;
            } else if (data.error && data.error.message && data.error.message.includes('getnewaddress')) {
              // Method not found, try deprecated getaccountaddress
              requestBody = {
                jsonrpc: '2.0',
                method: 'getaccountaddress',
                params: [''], // Default account
                id: 1,
              };
              
              response = await Promise.race([
                fetch(localRpcUrl, {
                  method: 'POST',
                  headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Basic ${auth}`,
                  },
                  body: JSON.stringify(requestBody),
                }),
                createTimeout(3000),
              ]);
              
              if (response.ok) {
                const fallbackData = await response.json();
                if (!fallbackData.error && fallbackData.result && typeof fallbackData.result === 'string') {
                  address = fallbackData.result;
                }
              }
            }
          }
          
          if (address && address.startsWith('R') && address.length === 34) {
            console.log('✅ [Ravencoin Wallet] Connected to local Ravencoin Core wallet:', address);
            toast.success('Ravencoin Core Connected', {
              description: `Using local Ravencoin Core wallet for transaction signing`,
              duration: 5000,
            });
            return { address };
          }
        } catch (error: any) {
          // Check if it's a CORS error (only on HTTPS sites)
          if (isHttps && error.message && (
            error.message.includes('CORS') ||
            error.message.includes('Failed to fetch') ||
            error.message.includes('NetworkError') ||
            error.message.includes('Mixed Content') ||
            error.message.includes('blocked')
          )) {
            // CORS blocked on HTTPS - provide helpful options
            throw new WalletConnectionError(
              'Cannot connect to localhost from HTTPS site. Options: 1) Install KawSwap Wallet Bridge extension, 2) Enter your RVN address manually in the swap interface (click "Add Address" button), or 3) Run DEX locally on http://localhost',
              'CORS_BLOCKED'
            );
          }
          // On localhost, errors mean wallet isn't running or wrong credentials
          // Try next credential set
          continue;
        }
      }
    } catch (localError: any) {
      if (localError instanceof WalletConnectionError && localError.code === 'CORS_BLOCKED') {
        throw localError; // Re-throw CORS errors
      }
      console.log('[Ravencoin Wallet] Local Ravencoin Core not available');
    }
    
    // NO VPS FALLBACK - VPS is for backend operations only
    // If no local wallet found, provide helpful error message
    let errorMessage: string;
    
    if (isLocalhost) {
      // On localhost - wallet should work directly if running
      errorMessage = 'Ravencoin Core wallet not detected. Please ensure Ravencoin Core is running with RPC enabled on port 8766 (check ravencoin.conf has rpcuser/rpcpassword set), or enter your RVN address manually in the swap interface.';
    } else if (isHttps) {
      // On HTTPS site - CORS blocks direct connection
      errorMessage = hasExtension()
        ? 'Ravencoin Core wallet not detected. Please ensure Ravencoin Core is running with RPC enabled on port 8766, or enter your RVN address manually in the swap interface.'
        : 'Cannot connect to localhost from HTTPS site. Options: 1) Install KawSwap Wallet Bridge extension, 2) Run DEX locally on http://localhost, or 3) Enter your RVN address manually in the swap interface.';
    } else {
      // Shouldn't happen, but handle it
      errorMessage = 'Cannot connect to Ravencoin Core. Please ensure it\'s running with RPC enabled on port 8766, or enter your RVN address manually in the swap interface.';
    }
    
    throw new WalletConnectionError(
      errorMessage,
      'NO_LOCAL_WALLET'
    );
    
  } catch (error: any) {
    if (error instanceof WalletConnectionError) {
      throw error;
    }
    console.error('Failed to connect to Ravencoin wallet:', error);
    throw new WalletConnectionError(
      'Ravencoin connection failed. You can still use RVN swaps by entering your address manually.',
      'RAVENCOIN_CONNECTION_FAILED'
    );
  }
}

// Helper to safely extract error messages - prevents binary data from being displayed
function getSafeErrorMessage(error: any, fallback: string = 'An error occurred'): string {
  if (!error) return fallback;
  
  // If error.message exists and is a reasonable string, use it
  if (error.message && typeof error.message === 'string') {
    // Check if it looks like binary data (long string of numbers/commas)
    if (error.message.length > 200 || /^[\d,\s\[\]"]+$/.test(error.message)) {
      return fallback;
    }
    return error.message;
  }
  
  // If error is a string itself
  if (typeof error === 'string') {
    if (error.length > 200 || /^[\d,\s\[\]"]+$/.test(error)) {
      return fallback;
    }
    return error;
  }
  
  return fallback;
}

export function handleWalletError(error: any): void {
  // Handle specific WalletConnect errors with user-friendly messages
  if (error instanceof WalletConnectionError) {
    if (error.code === 'FRAMES_DISALLOWED') {
      toast.error('WalletConnect Configuration Error', {
        description: 'Domain not whitelisted. Please add kawswap.io to your WalletConnect Cloud project at https://cloud.walletconnect.com',
        duration: 10000,
      });
      return;
    }
    if (error.code === 'INVALID_PROJECT_ID' || error.code === 'CONFIG_ERROR') {
      toast.error('WalletConnect Setup Required', {
        description: 'WalletConnect project ID not configured. Please set VITE_WALLETCONNECT_PROJECT_ID.',
        duration: 10000,
      });
      return;
    }
  }
  
  // Handle frames-disallowed in error message
  if (error?.message?.includes('frames-disallowed') || error?.message?.includes('dapp.frames-disallowed')) {
    toast.error('WalletConnect Configuration Error', {
      description: 'Domain not whitelisted. Please add kawswap.io to your WalletConnect Cloud project at https://cloud.walletconnect.com',
      duration: 10000,
    });
    return;
  }
  
  // Default error handling
  if (error instanceof WalletConnectionError) {
    switch (error.code) {
      case 'FRAMES_DISALLOWED':
        toast.error('WalletConnect Configuration Error', {
          description: 'Domain not whitelisted. Please add kawswap.io to your WalletConnect Cloud project at https://cloud.walletconnect.com',
          duration: 10000,
        });
        break;
      case 'INVALID_PROJECT_ID':
      case 'CONFIG_ERROR':
        toast.error('WalletConnect Setup Required', {
          description: 'WalletConnect project ID not configured. Please set VITE_WALLETCONNECT_PROJECT_ID.',
          duration: 10000,
        });
        break;
      case 'NO_PROVIDER':
        toast.error('Wallet Not Found', {
          description: 'Please install MetaMask or use a Web3-enabled browser.',
          duration: 5000,
        });
        break;
      case 'USER_REJECTED':
        toast.error('Connection Cancelled', {
          description: 'You rejected the wallet connection request.',
          duration: 4000,
        });
        break;
      case 'REQUEST_PENDING':
        toast.error('Request Pending', {
          description: 'Please check your wallet for a pending connection request.',
          duration: 5000,
        });
        break;
      case 'NO_ACCOUNTS':
        toast.error('No Accounts Found', {
          description: 'Please unlock your wallet and try again.',
          duration: 4000,
        });
        break;
      case 'TIMEOUT':
        toast.error('Connection Timeout', {
          description: 'The connection request timed out. Please try again.',
          duration: 5000,
        });
        break;
      case 'RPC_FAILED':
        toast.error('RPC Connection Failed', {
          description: 'Unable to connect to blockchain RPC endpoints. Please check your internet connection and try again.',
          duration: 6000,
        });
        break;
      case 'NO_TRUST_WALLET':
        toast.error('Trust Wallet Not Found', {
          description: 'Please use Trust Wallet browser or install the browser extension.',
          duration: 6000,
        });
        break;
      case 'RAVENCOIN_CONNECTION_FAILED':
        toast.error('Ravencoin Connection Failed', {
          description: `Unable to connect to Ravencoin Core via ${RPC_ENDPOINTS.ravencoin.primary}. Please ensure your wallet is running.`,
          duration: 6000,
        });
        break;
      default:
        toast.error('Connection Failed', {
          description: getSafeErrorMessage(error, 'Failed to connect wallet. Please try again.'),
          duration: 5000,
        });
    }
  } else {
    toast.error('Unexpected Error', {
      description: 'An unexpected error occurred. Please try again.',
      duration: 4000,
    });
  }
}

export async function checkRPCHealth(endpoint: string): Promise<boolean> {
  try {
    const response = await Promise.race([
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'eth_blockNumber',
          params: [],
          id: 1,
        }),
      }),
      createTimeout(RPC_TIMEOUT),
    ]);
    return response.ok;
  } catch (error) {
    console.error('RPC health check failed:', error);
    return false;
  }
}

export async function getNetworkInfo(): Promise<{ chainId: string; networkName: string }> {
  // Prefer window.ethereum (browser extension) for network info
  const provider = window.ethereum || getWalletConnectProvider();
  if (!provider) {
    throw new WalletConnectionError('Web3 provider not available', 'NO_PROVIDER');
  }

  try {
    const chainId = await provider.request({ method: 'eth_chainId' });
    
    const networkNames: Record<string, string> = {
      '0x1': 'Ethereum Mainnet',
      '0x3': 'Ropsten Testnet',
      '0x4': 'Rinkeby Testnet',
      '0x5': 'Goerli Testnet',
      '0x89': 'Polygon Mainnet',
      '0xa86a': 'Avalanche Mainnet',
      '0x38': 'BSC Mainnet',
    };

    return {
      chainId,
      networkName: networkNames[chainId] || `Unknown Network (${chainId})`,
    };
  } catch (error: any) {
    throw new WalletConnectionError('Failed to get network info', 'NETWORK_INFO_FAILED');
  }
}

export function getRavencoinEndpoint(): string {
  return RPC_ENDPOINTS.ravencoin.primary;
}

// ============================================
// Ravencoin Transaction Execution for HTLC
// ============================================

export interface RavencoinTransferParams {
  fromAddress: string;
  toAddress: string;
  assetName: string;
  amount: number; // Asset amount
}

export interface RavencoinSendParams {
  fromAddress: string;
  toAddress: string;
  amount: number; // RVN amount
}

/**
 * Prepare Ravencoin asset transfer transaction data
 * Note: Actual execution requires user's wallet to sign the transaction
 * This function prepares the transaction parameters
 */
export function prepareRavencoinAssetTransfer(params: RavencoinTransferParams): {
  method: string;
  params: any[];
  instructions: string;
} {
  return {
    method: 'transfer',
    params: [
      params.assetName,
      params.amount,
      params.toAddress
    ],
    instructions: `To complete the swap, execute this Ravencoin RPC command:\n\n` +
      `ravencoin-cli transfer "${params.assetName}" ${params.amount} "${params.toAddress}"\n\n` +
      `Or use your Ravencoin wallet to transfer ${params.amount} ${params.assetName} to ${params.toAddress}`
  };
}

/**
 * Prepare RVN send transaction data
 * Note: Actual execution requires user's wallet to sign the transaction
 */
export function prepareRavencoinSend(params: RavencoinSendParams): {
  method: string;
  params: any[];
  instructions: string;
} {
  return {
    method: 'sendtoaddress',
    params: [
      params.toAddress,
      params.amount
    ],
    instructions: `To complete the swap, execute this Ravencoin RPC command:\n\n` +
      `ravencoin-cli sendtoaddress "${params.toAddress}" ${params.amount}\n\n` +
      `Or use your Ravencoin wallet to send ${params.amount} RVN to ${params.toAddress}`
  };
}

/**
 * Execute Ravencoin asset transfer via RPC (requires unlocked wallet)
 * This is a direct RPC call - only works if user has RPC access
 */
export async function executeRavencoinAssetTransfer(
  params: RavencoinTransferParams,
  rpcUser?: string,
  rpcPassword?: string
): Promise<string> {
  const endpoint = RPC_ENDPOINTS.ravencoin.primary;
  
  try {
    const requestBody = {
      jsonrpc: '2.0',
      method: 'transfer',
      params: [params.assetName, params.amount, params.toAddress],
      id: 1,
    };

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    // Add basic auth if provided
    if (rpcUser && rpcPassword) {
      const auth = btoa(`${rpcUser}:${rpcPassword}`);
      headers['Authorization'] = `Basic ${auth}`;
    }

    const response = await Promise.race([
      fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      }),
      createTimeout(RPC_TIMEOUT),
    ]);

    if (!response.ok) {
      throw new Error(`RPC request failed with status ${response.status}`);
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error.message || 'RPC error');
    }

    if (data.result && typeof data.result === 'string') {
      return data.result; // Transaction ID
    }

    throw new Error('Invalid response from RPC');
  } catch (error: any) {
    console.error('Failed to execute Ravencoin asset transfer:', error);
    throw new WalletConnectionError(
      error.message || 'Failed to execute asset transfer',
      'TRANSFER_FAILED'
    );
  }
}

/**
 * Execute RVN send via RPC (requires unlocked wallet)
 */
export async function executeRavencoinSend(
  params: RavencoinSendParams,
  rpcUser?: string,
  rpcPassword?: string
): Promise<string> {
  const endpoint = RPC_ENDPOINTS.ravencoin.primary;
  
  try {
    const requestBody = {
      jsonrpc: '2.0',
      method: 'sendtoaddress',
      params: [params.toAddress, params.amount],
      id: 1,
    };

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    if (rpcUser && rpcPassword) {
      const auth = btoa(`${rpcUser}:${rpcPassword}`);
      headers['Authorization'] = `Basic ${auth}`;
    }

    const response = await Promise.race([
      fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      }),
      createTimeout(RPC_TIMEOUT),
    ]);

    if (!response.ok) {
      throw new Error(`RPC request failed with status ${response.status}`);
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error.message || 'RPC error');
    }

    if (data.result && typeof data.result === 'string') {
      return data.result; // Transaction ID
    }

    throw new Error('Invalid response from RPC');
  } catch (error: any) {
    console.error('Failed to execute Ravencoin send:', error);
    throw new WalletConnectionError(
      error.message || 'Failed to execute RVN send',
      'SEND_FAILED'
    );
  }
}
